import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import type { ChartRange } from './usePoolChart'
import { strictFetcher } from './strict-fetcher'

interface UsdCandle {
	ts: number
	open: number
	high: number
	low: number
	close: number
	volume: number
}

export interface TokenChartResponse {
	id: string
	name?: string
	range: ChartRange
	/// `usd` when a real dollar series covers this asset; `frbtc` means no
	/// USD series exists and the caller must fall back to the pool chart.
	currency: 'usd' | 'frbtc'
	/// `pizza` is the id-verified all-pools USD index; `hyperliquid` is
	/// BTC/USD, served only for frBTC (a 1:1 BTC wrap — its honest chart).
	source: 'pizza' | 'hyperliquid' | 'none'
	candles: UsdCandle[]
	change_pct?: number | null
}

/// An ASSET's price history in USD, aggregated across all its pools — the
/// chart a trader means when they ask what something has been doing. Empty
/// `candles` + `currency: 'frbtc'` is a real answer ("no USD coverage"), not
/// an error; the chart component falls back to the pool series for those.
/// A FAILED fetch is neither — `error` is surfaced so the chart can say
/// "couldn't load" instead of asserting "no history", and `retry` refetches.
export default function useTokenChart(
	assetId: string | null,
	range: ChartRange
): {
	data: TokenChartResponse | undefined
	isLoading: boolean
	error: unknown
	retry: () => void
} {
	const { data, isLoading, error, mutate } = useSwr<TokenChartResponse>(
		assetId ? `${API_URL}/alkane/token/${assetId}/chart?range=${range}` : null,
		strictFetcher,
		{ refreshInterval: 60_000, revalidateOnFocus: false, keepPreviousData: true }
	)
	return { data, isLoading, error, retry: () => void mutate() }
}
